const SURPRISE_FAQ = {
    q: 'Why can the price move the “wrong” way?',
    a: 'Prices already reflect the forecast, so markets react to the surprise, not the number itself. Other parts of the same release, revisions to earlier months or other news in the same hour can outweigh the headline figure, and a first move often reverses within the hour.',
};
const RISK_FAQ = {
    q: 'Should I close my trade before the release?',
    a: 'That is your decision; this page does not give advice. What is certain is that prices can jump in seconds around big releases, spreads widen, and a stop loss can close at a worse price than the one you set. Many traders check their position size and stop before the release.',
};
const guide = (b) => ({ ...b, faq: [SURPRISE_FAQ, ...(b.faq ?? []), RISK_FAQ] });
const rateDecision = (b) => guide({
    slug: b.slug,
    name: `${b.short} interest rate decision`,
    currency: b.ccy,
    category: 'central_banks',
    match: b.match,
    short: `${b.bank} sets ${b.country} interest rates`,
    publisher: b.publisher,
    schedule: b.schedule,
    unit: 'Interest rate, % a year',
    good: 'higher',
    basics: [
        `The ${b.bank} decides the interest rate that sets the cost of borrowing across ${b.country}. It raises rates to cool inflation and cuts them to support growth and jobs.`,
        `The decision comes with a statement explaining it, and often a press conference. Markets read both for hints about the next moves, which can matter more than the decision itself.`,
    ],
    read: {
        above: `A higher rate than expected, or a firmer message about inflation, often lifts the ${b.ccy} and can weigh on ${b.index}.`,
        below: `A lower rate than expected, or a softer message, often weakens the ${b.ccy} and can support ${b.index}.`,
    },
    deeper: [
        'Most decisions are fully priced in by the day: the forecast is rarely wrong about the rate itself. The surprise is usually in the statement, the vote split or the projections, which show how many cuts or rises policymakers expect.',
        b.deeper,
        `Watch ${b.pairs} for the currency's reaction, and government bond yields: a decision that moves expectations for later meetings moves two-year yields first.`,
    ],
    faq: [{ q: 'What does “priced in” mean?', a: 'Traders have already bought or sold on what they expect, so when the expected decision arrives, there is little left to react to. Only the part nobody expected moves prices.' }],
    related: b.related,
    lesson: 'rates-and-central-banks',
});
const cpi = (c) => guide({
    slug: c.slug,
    name: c.name,
    currency: c.ccy,
    category: 'inflation',
    match: c.match,
    short: `How fast prices are rising in ${c.country}`,
    publisher: c.publisher,
    schedule: c.schedule,
    unit: '% change, month on month or year on year',
    good: 'higher',
    basics: [
        `The consumer price index tracks what a typical basket of goods and services costs. Its yearly change is the inflation rate: 3% means prices are 3% higher than a year ago.`,
        `${c.bank} aims to keep inflation close to 2%. Inflation above target makes interest rate rises, or fewer cuts, more likely, which is why this release moves markets.`,
    ],
    read: {
        above: `Inflation above the forecast makes higher interest rates more likely. The ${c.ccy} often rises; shares and gold can come under pressure.`,
        below: `Inflation below the forecast makes rate cuts more likely. The ${c.ccy} often falls; shares can benefit.`,
    },
    deeper: [
        '“Core” inflation leaves out food and energy, whose prices swing with harvests and oil. Central banks watch core more closely because it shows the underlying trend.',
        'The month-on-month change shows the latest pressure; the year-on-year rate moves slowly because it compares with a year ago. A small difference matters: 0.1 percentage point above the forecast is a real surprise.',
        c.deeper,
    ],
    related: c.related,
    lesson: 'cpi-and-rate-decisions',
});
const jobs = (j) => guide({
    slug: j.slug,
    name: j.name,
    currency: j.ccy,
    category: 'jobs',
    match: j.match,
    short: `Jobs added and the unemployment rate in ${j.country}`,
    publisher: j.publisher,
    schedule: j.schedule,
    unit: 'Jobs added (thousands) · unemployment rate (%)',
    good: 'higher',
    basics: [
        `This report counts how many people found or lost work last month and what share of people looking for work have none: the unemployment rate.`,
        `A strong jobs market keeps spending and inflation up, which makes higher interest rates more likely. A weak one points the other way.`,
    ],
    read: {
        above: `More jobs than forecast, or lower unemployment, often lifts the ${j.ccy}.`,
        below: `Fewer jobs than forecast, or higher unemployment, often weakens the ${j.ccy}.`,
    },
    deeper: [
        'For the unemployment rate, lower is the good surprise; for jobs added, higher is. When the two disagree, markets usually follow the jobs figure and wage growth.',
        j.deeper,
    ],
    related: j.related,
    lesson: 'jobs-reports-big-releases',
});
const pmiBasics = (who) => [
    `Purchasing managers at ${who} are asked whether output, new orders, hiring and prices went up or down last month. The answers are combined into one index.`,
    'Above 50 means activity is growing; below 50 means it is shrinking. Because it comes out early in the month, it is one of the first readings of how the economy is doing.',
];
// ---- the guides ----
export const ECON_GUIDES = [
    // ---- United States ----
    guide({
        slug: 'us-crude-oil-inventories',
        name: 'US crude oil inventories',
        currency: 'USD',
        category: 'energy',
        match: /crude oil inventories|eia crude|crude oil stocks/i,
        short: 'Weekly change in US oil stocks, from the EIA',
        publisher: 'US Energy Information Administration (EIA)',
        schedule: 'Weekly, Wednesdays 10:30 New York time (Thursday after a Monday holiday)',
        unit: 'Millions of barrels, change on the week',
        good: 'lower',
        basics: [
            'Every week the US government counts how much crude oil companies hold in storage. The report says how much that changed since the week before.',
            'Think of it as the country’s oil pantry. If it fills up, there is more oil than buyers need right now; if it empties, demand is using oil faster than it arrives. Traders compare the change with the forecast, because prices already reflect the forecast.',
        ],
        read: {
            above: 'A rise in stocks, or a smaller fall than forecast, means looser supply. Oil prices often fall; USD/CAD often rises, as Canada exports oil.',
            below: 'A bigger fall in stocks than forecast means tighter supply. Oil prices often rise; USD/CAD often falls.',
        },
        deeper: [
            'The headline excludes the Strategic Petroleum Reserve. The same report gives gasoline and distillate (diesel and heating oil) stocks, refinery use and implied demand; on some weeks those move prices more than crude.',
            'Stocks at Cushing, Oklahoma, the delivery point for the WTI futures contract, matter for WTI in particular: low Cushing stocks can lift the nearest contract over later ones.',
            'The American Petroleum Institute (API) publishes its own count on Tuesday evening. A big gap between the API figure and the forecast often moves prices before the official report, so the EIA figure is then judged against both.',
        ],
        faq: [
            { q: 'What is the difference between the API and EIA figures?', a: 'The API is an industry group that surveys its members and publishes on Tuesday evening. The EIA is the government agency, with wider coverage, and publishes on Wednesday. The EIA figure is the official one; the two often differ by a million barrels or more.' },
            { q: 'What is the Strategic Petroleum Reserve?', a: 'An emergency oil store held by the US government. It is reported separately and left out of the headline figure, so releases from it do not count as a fall in commercial stocks.' },
        ],
        related: ['us-natural-gas-storage', 'opec-meetings', 'energy-data'],
        lesson: 'oil-opec-inventories',
    }),
    guide({
        slug: 'us-natural-gas-storage',
        name: 'US natural gas storage',
        currency: 'USD',
        category: 'energy',
        match: /natural gas storage/i,
        short: 'Weekly change in US gas in storage, from the EIA',
        publisher: 'US Energy Information Administration (EIA)',
        schedule: 'Weekly, Thursdays 10:30 New York time',
        unit: 'Billion cubic feet (Bcf), change on the week',
        good: 'lower',
        basics: [
            'Gas is pumped into storage from spring to autumn and drawn out over the winter to heat homes. This report says how much was added or taken out last week.',
            'Compared with the forecast, it shows whether supply and demand are tighter or looser than traders thought.',
        ],
        read: {
            above: 'A bigger injection, or smaller withdrawal, than forecast means looser supply. Gas prices often fall.',
            below: 'A smaller injection, or bigger withdrawal, than forecast means tighter supply. Gas prices often rise.',
        },
        deeper: [
            'Traders compare total storage with the five-year average for the same week: a big shortfall going into winter makes prices more sensitive to cold weather forecasts.',
            'Weather drives demand, so the forecast already includes last week’s temperatures; surprises usually come from power demand, exports of liquefied gas or production.',
        ],
        related: ['us-crude-oil-inventories', 'energy-data'],
        lesson: 'natural-gas-seasons',
    }),
    guide({
        slug: 'opec-meetings',
        name: 'OPEC+ meetings',
        currency: null,
        category: 'energy',
        match: /opec/i,
        short: 'Oil producers decide how much crude to pump',
        publisher: 'OPEC and allied producers (OPEC+)',
        schedule: 'About twice a year in full, with monthly calls of a smaller group',
        unit: 'Production targets, barrels a day',
        good: null,
        basics: [
            'OPEC+ is a group of oil-producing countries, led by Saudi Arabia and Russia, that agree how much oil each will pump. Pumping less supports prices; pumping more weighs on them.',
            'Decisions are often leaked or reported before the meeting ends, so prices can move during the day rather than at a set time.',
        ],
        read: { above: 'A bigger rise in output than expected, or an end to cuts, often weighs on oil prices.', below: 'Deeper cuts than expected, or an extension of cuts, often supports oil prices.' },
        deeper: ['Targets and real output differ: some members produce above their quota. Markets watch whether cuts are actually delivered in the months after a meeting, in the monthly production surveys.'],
        related: ['us-crude-oil-inventories', 'energy-data'],
        lesson: 'oil-opec-inventories',
    }),
    guide({
        slug: 'fomc-minutes',
        name: 'FOMC minutes',
        currency: 'USD',
        category: 'central_banks',
        match: /fomc (meeting )?minutes/i,
        short: 'Record of the Fed’s last rate meeting',
        publisher: 'US Federal Reserve',
        schedule: 'Three weeks after each Fed meeting, 14:00 New York time',
        unit: 'Text',
        good: null,
        basics: [
            'The minutes are a detailed record of what Fed policymakers discussed at their last interest rate meeting.',
            'They show how many members wanted faster or slower changes, which can shift expectations for the next meetings.',
        ],
        read: { above: 'Minutes that sound more worried about inflation than the statement did often lift the US dollar.', below: 'Minutes that sound more worried about growth or jobs often weaken the US dollar and can support shares and gold.' },
        deeper: ['Markets read for the balance of opinion (“many”, “several”, “a few” participants) and for discussion of the balance sheet. Because they come three weeks late, newer data can make them stale.'],
        related: ['fed-rate-decision', 'fed-chair-speech'],
        lesson: 'rates-and-central-banks',
    }),
    guide({
        slug: 'fed-chair-speech',
        name: 'Fed Chair speeches',
        currency: 'USD',
        category: 'speeches',
        match: /fed chair|powell/i,
        short: 'Hints about future US interest rates can move all markets',
        publisher: 'US Federal Reserve',
        schedule: 'As announced: testimony to Congress twice a year, plus speeches and conferences',
        unit: 'Text',
        good: null,
        basics: [
            'The Chair of the Federal Reserve leads the committee that sets US interest rates. When the Chair speaks about the economy, markets listen for hints about the next decisions.',
            'There is no number to compare with a forecast, so moves depend on tone: whether the Chair sounds more worried about inflation or about growth.',
        ],
        read: { above: 'A firmer tone on inflation (“more work to do”) often lifts the US dollar and weighs on shares and gold.', below: 'A softer tone (“risks to jobs”) often weakens the US dollar and can support shares and gold.' },
        deeper: ['Prepared remarks are released when the speech starts; questions afterwards often move markets more, because answers are less scripted. Speeches outside the blackout period before each meeting carry the most information.'],
        related: ['fed-rate-decision', 'fomc-minutes', 'central-bank-speeches'],
        lesson: 'rates-and-central-banks',
    }),
    rateDecision({
        slug: 'fed-rate-decision',
        bank: 'US Federal Reserve',
        short: 'Fed',
        ccy: 'USD',
        country: 'the United States',
        match: /federal funds rate|fomc statement|fomc press conference|fomc economic projections/i,
        publisher: 'Federal Open Market Committee (FOMC), US Federal Reserve',
        schedule: 'Eight meetings a year; decision 14:00 New York time, press conference 14:30',
        pairs: 'EUR/USD and USD/JPY',
        index: 'US shares and gold',
        deeper: 'Four times a year the Fed publishes its “dot plot”: each member’s view of where rates will be at the end of each year. A shift in the median dot can move markets more than the decision.',
        related: ['fomc-minutes', 'fed-chair-speech', 'us-cpi', 'us-pce'],
    }),
    cpi({
        slug: 'us-cpi',
        name: 'US consumer price index (CPI)',
        ccy: 'USD',
        country: 'the United States',
        match: /^(core )?cpi\b|consumer price index/i,
        publisher: 'US Bureau of Labor Statistics (BLS)',
        schedule: 'Monthly, around the middle of the month, 08:30 New York time',
        bank: 'The Federal Reserve',
        deeper: 'Shelter (rents) is about a third of the index and moves slowly; markets watch “supercore” (services excluding housing and energy) for wage-driven inflation. The Fed’s 2% target is set on the PCE index, which usually runs a little below CPI.',
        related: ['us-pce', 'us-ppi', 'fed-rate-decision'],
    }),
    guide({
        slug: 'us-pce',
        name: 'US PCE price index',
        currency: 'USD',
        category: 'inflation',
        match: /pce price index|personal consumption expenditures price/i,
        short: 'The Fed’s preferred measure of inflation',
        publisher: 'US Bureau of Economic Analysis (BEA)',
        schedule: 'Monthly, near the end of the month, 08:30 New York time',
        unit: '% change, month on month or year on year',
        good: 'higher',
        basics: [
            'Like CPI, the PCE index measures how fast prices are rising, but it covers more spending, including what is paid on people’s behalf, such as health care.',
            'The Federal Reserve’s 2% inflation target is set on this index, so a surprise here speaks directly to the Fed.',
        ],
        read: { above: 'Inflation above the forecast often lifts the US dollar and weighs on shares and gold.', below: 'Inflation below the forecast often weakens the US dollar and can support shares.' },
        deeper: ['Because CPI and PPI come out earlier in the month, economists can estimate PCE closely; surprises are usually small. The same release has personal income and spending.'],
        related: ['us-cpi', 'us-ppi', 'fed-rate-decision'],
        lesson: 'cpi-and-rate-decisions',
    }),
    guide({
        slug: 'us-ppi',
        name: 'US producer price index (PPI)',
        currency: 'USD',
        category: 'inflation',
        match: /\bppi\b|producer price/i,
        short: 'Prices businesses get for what they sell',
        publisher: 'US Bureau of Labor Statistics (BLS)',
        schedule: 'Monthly, the day before or after CPI, 08:30 New York time',
        unit: '% change, month on month',
        good: 'higher',
        basics: ['The PPI measures the prices producers receive. Rising costs for businesses can be passed on to shoppers later, so PPI is watched as an early sign of consumer inflation.'],
        read: { above: 'Producer prices above the forecast can point to higher inflation ahead and often lift the US dollar.', below: 'Producer prices below the forecast often weaken the US dollar.' },
        deeper: ['Some PPI components, such as airfares and medical care, feed straight into the PCE index, so economists update PCE estimates as soon as PPI is out.'],
        related: ['us-cpi', 'us-pce'],
        lesson: 'cpi-and-rate-decisions',
    }),
    jobs({
        slug: 'us-nonfarm-payrolls',
        name: 'US non-farm payrolls',
        ccy: 'USD',
        country: 'the United States',
        match: /^non-?farm (employment change|payrolls)|^nonfarm payrolls/i,
        publisher: 'US Bureau of Labor Statistics (BLS), Employment Situation report',
        schedule: 'Monthly, usually the first Friday, 08:30 New York time',
        deeper: 'The two previous months are revised every time, sometimes by more than the new month’s surprise. Average hourly earnings, in the same report, speak to inflation and can move markets as much as the headline.',
        related: ['us-unemployment-rate', 'us-average-hourly-earnings', 'us-adp-employment', 'us-jobless-claims'],
    }),
    guide({
        slug: 'us-unemployment-rate',
        name: 'US unemployment rate',
        currency: 'USD',
        category: 'jobs',
        match: /^unemployment rate/i,
        short: 'Share of people looking for work who have none',
        publisher: 'US Bureau of Labor Statistics (BLS), Employment Situation report',
        schedule: 'Monthly with payrolls, usually the first Friday, 08:30 New York time',
        unit: '% of the labour force',
        good: 'lower',
        basics: ['The unemployment rate is the share of people who want a job and are looking, but don’t have one. It comes from a survey of households, separate from the payrolls count of businesses.'],
        read: { above: 'Unemployment above the forecast points to a weaker jobs market; the US dollar often falls.', below: 'Unemployment below the forecast points to a tighter jobs market; the US dollar often rises.' },
        deeper: ['A rise because more people start looking for work (the participation rate) is less worrying than a rise from job losses. The household survey is small, so a 0.1-point move can be noise.'],
        related: ['us-nonfarm-payrolls', 'us-jobless-claims'],
        lesson: 'jobs-reports-big-releases',
    }),
    guide({
        slug: 'us-average-hourly-earnings',
        name: 'US average hourly earnings',
        currency: 'USD',
        category: 'jobs',
        match: /average hourly earnings/i,
        short: 'How fast US pay is rising',
        publisher: 'US Bureau of Labor Statistics (BLS), Employment Situation report',
        schedule: 'Monthly with payrolls, usually the first Friday, 08:30 New York time',
        unit: '% change, month on month',
        good: 'higher',
        basics: ['This measures how much pay per hour went up last month. Fast wage growth can keep inflation high, which matters for interest rates.'],
        read: { above: 'Pay rising faster than forecast often lifts the US dollar.', below: 'Pay rising slower than forecast often weakens the US dollar.' },
        deeper: ['Changes in the mix of jobs distort it: if many low-paid workers lose jobs, the average rises without anyone earning more. The quarterly Employment Cost Index adjusts for this.'],
        related: ['us-nonfarm-payrolls', 'us-cpi'],
        lesson: 'jobs-reports-big-releases',
    }),
    guide({
        slug: 'us-jobless-claims',
        name: 'US initial jobless claims',
        currency: 'USD',
        category: 'jobs',
        match: /unemployment claims|jobless claims/i,
        short: 'New claims for unemployment benefit last week',
        publisher: 'US Department of Labor',
        schedule: 'Weekly, Thursdays 08:30 New York time',
        unit: 'Claims (thousands)',
        good: 'lower',
        basics: ['Each week this counts how many people applied for unemployment benefit for the first time. It is the most up-to-date sign of layoffs.'],
        read: { above: 'More claims than forecast point to rising layoffs; the US dollar often falls.', below: 'Fewer claims than forecast point to a firm jobs market; the US dollar often rises.' },
        deeper: ['Weekly numbers are noisy around holidays and storms; the four-week average shows the trend. Continuing claims show how fast people who lost jobs find new ones.'],
        related: ['us-nonfarm-payrolls', 'us-unemployment-rate'],
        lesson: 'jobs-reports-big-releases',
    }),
    guide({
        slug: 'us-jolts',
        name: 'US job openings (JOLTS)',
        currency: 'USD',
        category: 'jobs',
        match: /jolts/i,
        short: 'How many jobs US employers want to fill',
        publisher: 'US Bureau of Labor Statistics (BLS)',
        schedule: 'Monthly, early in the month (data two months old), 10:00 New York time',
        unit: 'Job openings (millions)',
        good: 'higher',
        basics: ['JOLTS counts vacancies: jobs employers are trying to fill. Many openings for each person out of work means a tight jobs market and pressure on wages.'],
        read: { above: 'More openings than forecast often lift the US dollar.', below: 'Fewer openings than forecast often weaken the US dollar.' },
        deeper: ['The quits rate in the same report shows how confident workers are about finding another job; falling quits usually lead slower wage growth.'],
        related: ['us-nonfarm-payrolls', 'us-unemployment-rate'],
        lesson: 'jobs-reports-big-releases',
    }),
    guide({
        slug: 'us-adp-employment',
        name: 'US ADP employment',
        currency: 'USD',
        category: 'jobs',
        match: /\badp\b/i,
        short: 'Private-sector jobs added, from payroll firm ADP',
        publisher: 'ADP Research',
        schedule: 'Monthly, the Wednesday before payrolls, 08:15 New York time',
        unit: 'Jobs added (thousands)',
        good: 'higher',
        basics: ['ADP processes pay for millions of US workers and publishes its own count of private-sector jobs two days before the official payrolls report.'],
        read: { above: 'More jobs than forecast often lift the US dollar a little.', below: 'Fewer jobs than forecast often weaken the US dollar a little.' },
        deeper: ['ADP and the official payrolls figure often differ by 50,000 jobs or more, so markets treat ADP as a hint rather than a preview.'],
        related: ['us-nonfarm-payrolls', 'us-jobless-claims'],
        lesson: 'jobs-reports-big-releases',
    }),
    guide({
        slug: 'us-gdp',
        name: 'US GDP',
        currency: 'USD',
        category: 'growth',
        match: /\bgdp\b/i,
        short: 'How fast the US economy grew last quarter',
        publisher: 'US Bureau of Economic Analysis (BEA)',
        schedule: 'Quarterly: advance, second and third estimates, 08:30 New York time',
        unit: '% change, quarter on quarter at an annual rate',
        good: 'higher',
        basics: [
            'Gross domestic product is the value of everything the country produced. Its growth rate is the broadest measure of how the economy is doing.',
            'US figures are “annualised”: 2% means the economy would grow 2% in a year if it kept that pace for four quarters.',
        ],
        read: { above: 'Growth above the forecast often lifts the US dollar and can support shares.', below: 'Growth below the forecast often weakens the US dollar.' },
        deeper: ['The advance estimate moves markets most; later estimates revise it. Inventories and trade swing the headline; final sales to private domestic buyers show underlying demand.'],
        related: ['us-retail-sales', 'us-ism-manufacturing', 'us-ism-services'],
        lesson: 'jobs-reports-big-releases',
    }),
    guide({
        slug: 'us-retail-sales',
        name: 'US retail sales',
        currency: 'USD',
        category: 'consumer',
        match: /retail sales/i,
        short: 'How much Americans spent in shops last month',
        publisher: 'US Census Bureau',
        schedule: 'Monthly, around the 15th, 08:30 New York time',
        unit: '% change, month on month',
        good: 'higher',
        basics: ['Consumer spending is about two-thirds of the US economy. Retail sales show how much people spent in shops, online and at restaurants.'],
        read: { above: 'Spending above the forecast often lifts the US dollar.', below: 'Spending below the forecast often weakens the US dollar.' },
        deeper: ['Figures are not adjusted for prices, so high inflation flatters them. The “control group”, excluding cars, fuel, building materials and food services, feeds into GDP and is watched most closely.'],
        related: ['us-gdp', 'us-consumer-sentiment'],
        lesson: 'jobs-reports-big-releases',
    }),
    guide({
        slug: 'us-ism-manufacturing',
        name: 'US ISM manufacturing PMI',
        currency: 'USD',
        category: 'business',
        match: /ism manufacturing/i,
        short: 'Health of US factories; above 50 means growing',
        publisher: 'Institute for Supply Management (ISM)',
        schedule: 'Monthly, first business day, 10:00 New York time',
        unit: 'Index (50 = no change)',
        good: 'higher',
        basics: pmiBasics('US factories'),
        read: { above: 'A reading above the forecast often lifts the US dollar and shares.', below: 'A reading below the forecast often weakens the US dollar.' },
        deeper: ['The prices-paid part is an early sign of goods inflation; new orders lead production by a few months.'],
        related: ['us-ism-services', 'us-flash-pmi'],
        lesson: 'jobs-reports-big-releases',
    }),
    guide({
        slug: 'us-ism-services',
        name: 'US ISM services PMI',
        currency: 'USD',
        category: 'business',
        match: /ism (services|non-?manufacturing)/i,
        short: 'Health of US service businesses; above 50 means growing',
        publisher: 'Institute for Supply Management (ISM)',
        schedule: 'Monthly, third business day, 10:00 New York time',
        unit: 'Index (50 = no change)',
        good: 'higher',
        basics: pmiBasics('US service businesses, from shops to banks,'),
        read: { above: 'A reading above the forecast often lifts the US dollar.', below: 'A reading below the forecast often weakens the US dollar.' },
        deeper: ['Services are most of the US economy and drive most inflation now, so the prices-paid and employment parts get close attention.'],
        related: ['us-ism-manufacturing', 'us-flash-pmi'],
        lesson: 'jobs-reports-big-releases',
    }),
    guide({
        slug: 'us-flash-pmi',
        name: 'US flash PMIs',
        currency: 'USD',
        category: 'business',
        match: /flash (manufacturing|services|composite) pmi/i,
        short: 'Early survey of US business activity this month',
        publisher: 'S&P Global',
        schedule: 'Monthly, around the 23rd, 09:45 New York time',
        unit: 'Index (50 = no change)',
        good: 'higher',
        basics: pmiBasics('US companies'),
        read: { above: 'A reading above the forecast often lifts the US dollar.', below: 'A reading below the forecast often weakens the US dollar.' },
        deeper: ['“Flash” means an early estimate from most of the survey; the final reading a week later rarely changes much. The ISM surveys usually move markets more.'],
        related: ['us-ism-manufacturing', 'us-ism-services'],
        lesson: 'jobs-reports-big-releases',
    }),
    guide({
        slug: 'us-consumer-sentiment',
        name: 'University of Michigan consumer sentiment',
        currency: 'USD',
        category: 'consumer',
        match: /uom|michigan/i,
        short: 'How confident US households feel about money',
        publisher: 'University of Michigan',
        schedule: 'Monthly: preliminary mid-month, final at the end of the month, 10:00 New York time',
        unit: 'Index',
        good: 'higher',
        basics: ['A survey asks households about their finances and the economy. Confident households tend to spend more.'],
        read: { above: 'Sentiment above the forecast often lifts the US dollar a little.', below: 'Sentiment below the forecast often weakens the US dollar a little.' },
        deeper: ['The survey’s inflation expectations, one and five years ahead, are watched by the Fed and can move markets more than sentiment itself.'],
        related: ['us-retail-sales', 'consumer-data'],
        lesson: 'reading-the-economic-calendar',
    }),
    // ---- euro area ----
    rateDecision({
        slug: 'ecb-rate-decision',
        bank: 'European Central Bank',
        short: 'ECB',
        ccy: 'EUR',
        country: 'the euro area',
        match: /main refinancing rate|deposit facility rate|monetary policy statement|ecb press conference/i,
        publisher: 'European Central Bank (ECB)',
        schedule: 'Eight meetings a year; decision 14:15 Frankfurt time, press conference 14:45',
        pairs: 'EUR/USD and EUR/GBP',
        index: 'European shares (DAX, Euro Stoxx 50)',
        deeper: 'The deposit facility rate, what banks earn on money left at the ECB, is the rate that steers markets now. The ECB publishes staff projections four times a year.',
        related: ['eurozone-cpi', 'eurozone-pmi'],
    }),
    cpi({
        slug: 'eurozone-cpi',
        name: 'Euro area inflation (HICP)',
        ccy: 'EUR',
        country: 'the euro area',
        match: /cpi|hicp/i,
        publisher: 'Eurostat; German, French, Italian and Spanish figures from national statistics offices',
        schedule: 'Monthly flash estimate at the end or start of the month, 11:00 Frankfurt time',
        bank: 'The European Central Bank',
        deeper: 'Germany, France, Spain and Italy publish their own figures the day before or the same morning, so much of the euro area surprise is often known before the Eurostat release.',
        related: ['ecb-rate-decision', 'eurozone-pmi'],
    }),
    guide({
        slug: 'eurozone-pmi',
        name: 'Euro area PMIs',
        currency: 'EUR',
        category: 'business',
        match: /pmi/i,
        short: 'Survey of euro area business activity; above 50 means growing',
        publisher: 'HCOB and S&P Global',
        schedule: 'Monthly: flash around the 23rd, finals early the next month, from 09:15 Frankfurt time',
        unit: 'Index (50 = no change)',
        good: 'higher',
        basics: pmiBasics('companies in Germany, France and the rest of the euro area'),
        read: { above: 'A reading above the forecast often lifts the euro.', below: 'A reading below the forecast often weakens the euro.' },
        deeper: ['France, then Germany, then the euro area publish within 45 minutes of each other; the German manufacturing figure often sets the tone.'],
        related: ['german-ifo', 'german-zew', 'ecb-rate-decision'],
        lesson: 'reading-the-economic-calendar',
    }),
    guide({
        slug: 'german-ifo',
        name: 'German Ifo business climate',
        currency: 'EUR',
        category: 'business',
        match: /ifo/i,
        short: 'How German businesses see their prospects',
        publisher: 'Ifo Institute, Munich',
        schedule: 'Monthly, around the 25th, 10:00 Frankfurt time',
        unit: 'Index',
        good: 'higher',
        basics: ['About 9,000 German firms say how business is now and how they expect it to be in six months. Germany is the euro area’s biggest economy.'],
        read: { above: 'A reading above the forecast often lifts the euro and German shares.', below: 'A reading below the forecast often weakens the euro.' },
        deeper: ['The expectations part tends to lead the current-conditions part by a few months.'],
        related: ['german-zew', 'eurozone-pmi'],
        lesson: 'reading-the-economic-calendar',
    }),
    guide({
        slug: 'german-zew',
        name: 'German ZEW economic sentiment',
        currency: 'EUR',
        category: 'business',
        match: /zew/i,
        short: 'How financial analysts see Germany’s next six months',
        publisher: 'ZEW, Mannheim',
        schedule: 'Monthly, a Tuesday mid-month, 11:00 Frankfurt time',
        unit: 'Index (positive = more optimists than pessimists)',
        good: 'higher',
        basics: ['The ZEW asks financial analysts whether they expect the German economy to improve or worsen over six months.'],
        read: { above: 'A reading above the forecast often lifts the euro.', below: 'A reading below the forecast often weakens the euro.' },
        deeper: ['Because it surveys market professionals, it often follows share prices; markets give it less weight than the Ifo survey of firms.'],
        related: ['german-ifo', 'eurozone-pmi'],
        lesson: 'reading-the-economic-calendar',
    }),
    // ---- United Kingdom ----
    rateDecision({
        slug: 'boe-rate-decision',
        bank: 'Bank of England',
        short: 'BoE',
        ccy: 'GBP',
        country: 'the United Kingdom',
        match: /official bank rate|monetary policy (summary|report)|mpc|boe/i,
        publisher: 'Monetary Policy Committee (MPC), Bank of England',
        schedule: 'Eight meetings a year, 12:00 London time',
        pairs: 'GBP/USD and EUR/GBP',
        index: 'UK shares (FTSE 100)',
        deeper: 'The nine members vote individually and the split is published with the decision: a 5–4 vote says more about the next meeting than the decision itself. Many FTSE 100 companies earn abroad, so a weaker pound can lift the index.',
        related: ['uk-cpi', 'uk-jobs', 'uk-gdp'],
    }),
    cpi({
        slug: 'uk-cpi',
        name: 'UK consumer price index (CPI)',
        ccy: 'GBP',
        country: 'the United Kingdom',
        match: /cpi/i,
        publisher: 'Office for National Statistics (ONS)',
        schedule: 'Monthly, a Wednesday mid-month, 07:00 London time',
        bank: 'The Bank of England',
        deeper: 'Services inflation is the part the Bank of England watches most, because it follows wages. Regulated energy prices change every three months, which can cause step changes.',
        related: ['boe-rate-decision', 'uk-jobs'],
    }),
    guide({
        slug: 'uk-gdp',
        name: 'UK GDP',
        currency: 'GBP',
        category: 'growth',
        match: /gdp/i,
        short: 'How fast the UK economy grew',
        publisher: 'Office for National Statistics (ONS)',
        schedule: 'Monthly estimate and quarterly figures, 07:00 London time',
        unit: '% change, month on month or quarter on quarter',
        good: 'higher',
        basics: ['Gross domestic product is the value of everything the UK produced. The UK is one of few countries to publish it every month as well as every quarter.'],
        read: { above: 'Growth above the forecast often lifts the pound.', below: 'Growth below the forecast often weakens the pound.' },
        deeper: ['Monthly figures are volatile (strikes, weather, bank holidays); the three-month average is the steadier read.'],
        related: ['boe-rate-decision', 'uk-cpi'],
        lesson: 'reading-the-economic-calendar',
    }),
    jobs({
        slug: 'uk-jobs',
        name: 'UK jobs and pay',
        ccy: 'GBP',
        country: 'the United Kingdom',
        match: /claimant count|unemployment rate|average earnings|employment change/i,
        publisher: 'Office for National Statistics (ONS)',
        schedule: 'Monthly, a Tuesday mid-month, 07:00 London time',
        deeper: 'Pay growth (average earnings) matters most to the Bank of England. The survey behind the unemployment rate has had low response rates, so markets lean on payroll and tax data in the same release.',
        related: ['boe-rate-decision', 'uk-cpi'],
    }),
    // ---- Japan ----
    rateDecision({
        slug: 'boj-rate-decision',
        bank: 'Bank of Japan',
        short: 'BoJ',
        ccy: 'JPY',
        country: 'Japan',
        match: /boj|monetary policy statement|policy rate/i,
        publisher: 'Bank of Japan (BoJ)',
        schedule: 'Eight meetings a year; no set time, usually around midday Tokyo time',
        pairs: 'USD/JPY and EUR/JPY',
        index: 'Japanese shares (Nikkei 225)',
        deeper: 'Japan kept rates near or below zero for many years, so each step towards normal rates has moved the yen sharply. The decision has no fixed time, which makes the reaction harder to plan for.',
        related: ['japan-cpi'],
    }),
    cpi({
        slug: 'japan-cpi',
        name: 'Japan consumer prices',
        ccy: 'JPY',
        country: 'Japan',
        match: /cpi/i,
        publisher: 'Statistics Bureau of Japan',
        schedule: 'Tokyo figures at the end of each month, national three weeks later, 08:30 Tokyo time',
        bank: 'The Bank of Japan',
        deeper: 'The Tokyo figures come three weeks before the national ones and usually point the same way, so they move markets more. Japan’s “core” leaves out fresh food only.',
        related: ['boj-rate-decision'],
    }),
    // ---- Canada ----
    rateDecision({
        slug: 'boc-rate-decision',
        bank: 'Bank of Canada',
        short: 'BoC',
        ccy: 'CAD',
        country: 'Canada',
        match: /overnight rate|boc/i,
        publisher: 'Bank of Canada (BoC)',
        schedule: 'Eight meetings a year, 09:45 Toronto time',
        pairs: 'USD/CAD',
        index: 'Canadian shares',
        deeper: 'Canada’s economy is closely tied to the US and to oil, so the Bank of Canada’s path usually follows the Fed’s with a lag; USD/CAD often reacts to oil prices as much as to the decision.',
        related: ['canada-jobs', 'canada-cpi'],
    }),
    jobs({
        slug: 'canada-jobs',
        name: 'Canada employment',
        ccy: 'CAD',
        country: 'Canada',
        match: /employment change|unemployment rate/i,
        publisher: 'Statistics Canada, Labour Force Survey',
        schedule: 'Monthly, usually the first Friday, 08:30 Toronto time',
        deeper: 'It often comes out at the same minute as the US payrolls report, so USD/CAD can move on both at once.',
        related: ['boc-rate-decision', 'canada-cpi'],
    }),
    cpi({
        slug: 'canada-cpi',
        name: 'Canada consumer price index (CPI)',
        ccy: 'CAD',
        country: 'Canada',
        match: /cpi/i,
        publisher: 'Statistics Canada',
        schedule: 'Monthly, mid-month, 08:30 Toronto time',
        bank: 'The Bank of Canada',
        deeper: 'The Bank of Canada watches its own core measures, “trim” and “median”, which strip out the most extreme price changes.',
        related: ['boc-rate-decision', 'canada-jobs'],
    }),
    // ---- Australia and New Zealand ----
    rateDecision({
        slug: 'rba-rate-decision',
        bank: 'Reserve Bank of Australia',
        short: 'RBA',
        ccy: 'AUD',
        country: 'Australia',
        match: /cash rate|rba/i,
        publisher: 'Reserve Bank of Australia (RBA)',
        schedule: 'Eight meetings a year, 14:30 Sydney time',
        pairs: 'AUD/USD',
        index: 'Australian shares (ASX 200)',
        deeper: 'The Australian dollar also follows China’s economy and metal prices, as Australia exports iron ore and coal to China.',
        related: ['australia-jobs', 'australia-cpi', 'china-pmi'],
    }),
    jobs({
        slug: 'australia-jobs',
        name: 'Australia employment',
        ccy: 'AUD',
        country: 'Australia',
        match: /employment change|unemployment rate/i,
        publisher: 'Australian Bureau of Statistics (ABS)',
        schedule: 'Monthly, mid-month, 11:30 Sydney time',
        deeper: 'Full-time and part-time jobs are reported separately; a rise made only of part-time jobs is read as weaker.',
        related: ['rba-rate-decision', 'australia-cpi'],
    }),
    cpi({
        slug: 'australia-cpi',
        name: 'Australia consumer price index (CPI)',
        ccy: 'AUD',
        country: 'Australia',
        match: /cpi/i,
        publisher: 'Australian Bureau of Statistics (ABS)',
        schedule: 'Monthly, at the end of the month, 11:30 Sydney time',
        bank: 'The Reserve Bank of Australia',
        deeper: 'The RBA focuses on the trimmed mean, which drops the biggest price rises and falls each period.',
        related: ['rba-rate-decision', 'australia-jobs'],
    }),
    rateDecision({
        slug: 'rbnz-rate-decision',
        bank: 'Reserve Bank of New Zealand',
        short: 'RBNZ',
        ccy: 'NZD',
        country: 'New Zealand',
        match: /official cash rate|rbnz/i,
        publisher: 'Reserve Bank of New Zealand (RBNZ)',
        schedule: 'Seven meetings a year, 14:00 Wellington time',
        pairs: 'NZD/USD',
        index: 'New Zealand shares',
        deeper: 'The RBNZ publishes its own forecast path for the cash rate, so markets compare the new path with the last one.',
        related: ['rba-rate-decision'],
    }),
    // ---- Switzerland ----
    rateDecision({
        slug: 'snb-rate-decision',
        bank: 'Swiss National Bank',
        short: 'SNB',
        ccy: 'CHF',
        country: 'Switzerland',
        match: /snb|policy rate|monetary policy assessment/i,
        publisher: 'Swiss National Bank (SNB)',
        schedule: 'Four meetings a year, 09:30 Zurich time',
        pairs: 'USD/CHF and EUR/CHF',
        index: 'Swiss shares',
        deeper: 'The SNB also buys or sells currencies to steer the franc, which is a safe haven; what it says about currency intervention can matter more than the rate.',
        related: ['ecb-rate-decision'],
    }),
    // ---- China ----
    guide({
        slug: 'china-pmi',
        name: 'China PMIs',
        currency: 'CNY',
        category: 'business',
        match: /pmi/i,
        short: 'Survey of Chinese factories; above 50 means growing',
        publisher: 'National Bureau of Statistics (NBS); a private survey from S&P Global and RatingDog',
        schedule: 'Monthly, the last day of the month and the first business days after, 09:30 Beijing time',
        unit: 'Index (50 = no change)',
        good: 'higher',
        basics: pmiBasics('Chinese factories and service firms'),
        read: { above: 'A reading above the forecast often lifts the Australian dollar, copper and Asian shares.', below: 'A reading below the forecast often weighs on the Australian dollar, copper and oil.' },
        deeper: ['The official survey leans towards big state firms; the private survey covers more small and export-oriented firms. Chinese New Year distorts January and February.'],
        related: ['china-cpi', 'china-trade-balance', 'rba-rate-decision'],
        lesson: 'silver-industrial-metals',
    }),
    cpi({
        slug: 'china-cpi',
        name: 'China consumer price index (CPI)',
        ccy: 'CNY',
        country: 'China',
        match: /cpi|ppi/i,
        publisher: 'National Bureau of Statistics (NBS)',
        schedule: 'Monthly, around the 9th, 09:30 Beijing time',
        bank: 'The People’s Bank of China',
        deeper: 'In China the concern has often been prices rising too slowly or falling, not too fast; producer prices (PPI), out at the same time, show factory-gate deflation.',
        related: ['china-pmi', 'china-trade-balance'],
    }),
    guide({
        slug: 'china-trade-balance',
        name: 'China trade balance',
        currency: 'CNY',
        category: 'trade',
        match: /trade balance|exports|imports/i,
        short: 'China’s exports minus imports',
        publisher: 'General Administration of Customs of China',
        schedule: 'Monthly, around the 7th to 10th, no fixed time',
        unit: 'US dollars (billions) · % change a year',
        good: 'higher',
        basics: ['This shows how much China sold abroad and bought from abroad. Exports show world demand for Chinese goods; imports show China’s own demand for oil, metals and food.'],
        read: { above: 'Strong exports and imports often support the Australian dollar, copper and oil.', below: 'Weak figures often weigh on the Australian dollar, copper and oil.' },
        deeper: ['Import volumes of crude oil, iron ore and copper, released the same day, are watched by commodity traders more than the headline balance.'],
        related: ['china-pmi', 'china-cpi'],
        lesson: 'silver-industrial-metals',
    }),
    // ---- category guides: every event without its own guide opens one of these ----
    ...[
        ['central-bank-decisions', 'central_banks', 'Central bank decisions', 'A central bank decides interest rates or explains its plans', 'Interest rates are the price of borrowing money. Central banks raise them to cool inflation and cut them to support growth. Decisions, statements and minutes all move the currency.'],
        ['central-bank-speeches', 'speeches', 'Central bank speeches', 'A policymaker speaks; hints about rates can move markets', 'When people who set interest rates speak, markets listen for hints about the next decisions. There is no number to compare: what matters is whether they sound more worried about inflation or about growth.'],
        ['inflation-data', 'inflation', 'Inflation data', 'How fast prices are rising', 'Inflation figures show how fast prices are rising. Higher than expected makes higher interest rates more likely, which usually supports the currency.'],
        ['jobs-data', 'jobs', 'Jobs data', 'How many people are working and how pay is growing', 'A strong jobs market keeps spending and inflation up, which makes higher interest rates more likely. Weak jobs data points the other way.'],
        ['growth-data', 'growth', 'Growth data', 'How fast the economy or industry is growing', 'Growth figures, such as GDP and industrial production, show how fast the economy is expanding. Stronger growth usually supports the currency and local shares.'],
        ['business-surveys', 'business', 'Business surveys', 'How businesses see conditions; above 50 usually means growing', 'Surveys ask firms whether output, orders and hiring are rising or falling. They come out early, so they are among the first signs of a turn in the economy.'],
        ['consumer-data', 'consumer', 'Consumer data', 'How much households spend and how confident they feel', 'Household spending is the biggest part of most economies. Spending and confidence figures show whether it is holding up.'],
        ['housing-data', 'housing', 'Housing data', 'Home sales, building and house prices', 'Housing reacts quickly to interest rates, so home sales and building figures show how higher or lower rates are feeding through.'],
        ['trade-data', 'trade', 'Trade data', 'What a country sells abroad and buys from abroad', 'Trade figures show demand for a country’s goods abroad and its own demand for imports. They matter most for big exporters.'],
        ['energy-data', 'energy', 'Energy data', 'Oil and gas stocks, output and producer decisions', 'Energy reports show whether oil and gas supply is running ahead of demand or behind it, which moves oil and gas prices and the currencies of exporters such as Canada and Norway.'],
        ['bond-auctions', 'bonds', 'Government bond auctions', 'A government borrows by selling bonds', 'Governments borrow by auctioning bonds. Weak demand at an auction can push yields up, which can move the currency and shares.'],
        ['market-holidays', 'holidays', 'Market holidays', 'A public holiday: some markets close or trade thinly', 'On a public holiday local exchanges close or open for shorter hours, and banks are shut. Trading is thinner, so spreads can be wider and moves bigger than usual.'],
        ['other-releases', 'other', 'Other releases', 'An economic release outside the main categories', 'Smaller releases rarely move markets on their own, but a big surprise can still shift the currency, especially on a quiet day.'],
    ].map(([slug, category, name, short, intro]) => ({
        ...guide({
            slug,
            name,
            currency: null,
            category,
            match: null,
            short,
            publisher: 'Statistics offices, central banks and governments',
            schedule: 'Varies by country; the calendar shows each release’s time',
            unit: 'Varies',
            good: category === 'holidays' || category === 'speeches' || category === 'central_banks' ? null : 'higher',
            basics: [intro, 'Markets compare each figure with its forecast: prices already reflect the forecast, so the surprise is what moves them. Impact bars show how much a release usually matters.'],
            read: {
                above: 'A figure better than forecast for the economy usually supports the currency.',
                below: 'A figure worse than forecast for the economy usually weighs on the currency.',
            },
            deeper: ['High-impact releases cluster around the same times each month. Several releases at the same minute can pull in different directions, so the reaction follows the one markets care about most at the time.'],
            related: ['us-cpi', 'fed-rate-decision', 'us-nonfarm-payrolls'],
            lesson: 'reading-the-economic-calendar',
        }),
        generic: true,
    })),
];
//# sourceMappingURL=econ-guides.js.map